/**
 * useCoworkerChat — live-source adapter that lets coworker threads render
 * through the regular ChatView (docs/COWORKER_CONVERSATION_UNIFICATION.md,
 * Phase 2).
 *
 * Responsibilities:
 *   - follow the linked task's execution event stream (EventSource on web,
 *     fetch SSE + JSON polling on native);

 *   - keep the chat's `isInferencing` store flag in sync with task liveness,
 *     so the standard composer (Stop button, send gating) "just works";
 *   - project the transcript rows (lib/workTranscript.js): the server rows
 *     from chat.messages up to `workSync.eventsSyncedSeq`, then rows derived
 *     from transcript events past that cursor, the live assistant draft,
 *     pending composer sends, and queued follow-ups;
 *   - refetch the conversation detail only when the server can add something
 *     the event log cannot (session_end, materialized artifact Files);
 *   - expose browser-login / confirmation / question state and send / stop actions.
 *
 * Nothing here writes rows into the chat store. The chat store holds the
 * server projection only; everything live is derived.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useAuthStore, useChatStore, useCoworkerStore, useFileStore, useModalStore, useToastStore } from '@/store';
import { findTaskIdForConversation } from '@/store/coworker';
import { chatsApi, coworkerApi } from '@/lib/api';
import { soundForWorkEvents } from '@/lib/workNotificationSound';
import { signalWorkTabAttention } from '@/lib/workTabAttention';
import { openWorkEventStream } from '@/lib/workEventStream';
import {
  isNewWorkEvent,
  isStaleWorkSseFrame,
  isWorkBlindContinue,
  isWorkResumable,
  isWorkScriptApprovalQuestion,
  seedWorkTaskStatus,
  workIsActivelyRunning,
  workActivityMetaFromEvents,
  workLifecycleStatusFromEvents,
  workPendingInteractionFromEvents,
  workPlanFromEvents,
  workQueuedDirectionCount,
} from '@/constants/work';
import { workFollowUpMessageId } from '@/constants/work_followup_queue';
import {
  applyWorkAssistantEvent,
  clearWorkAssistantStream,
  hasOpenWorkAssistantDraft,
} from '@/lib/workAssistantStream';
import {
  createWorkTranscriptMemo,
  projectWorkTranscript,
  workTranscriptSinceSeq,
} from '@/lib/workTranscript';
import { nextWorkLatch } from '@/lib/workModeLatch';
import { useRouter } from 'expo-router';
import { hasPendingBrowserInteraction, workBrowserActivity } from '@/lib/workBrowserActivity';
import { navigateToCreatedWork } from '@/lib/startWorkFromPrompt';
import { boundaryDestination, workBoundaryRequestId, finishWorkBoundaryRequest } from '@/lib/workTaskBoundary';
import { openWorkUndo } from '@/lib/workUndo';

export const COWORKER_ACTIVE_STATUSES = ['queued', 'running'];

const NO_EVENTS = [];
const NO_FOLLOWUPS = [];
const NO_ROWS = [];

function filesFromIds(fileIds) {
  return (fileIds || []).flatMap((id) => {
    const file = useFileStore.getState().files?.[id];
    return file ? [file] : [];
  });
}

// A quiet stream is normal (a single research tool call can run for a while),
// a dead one is not, and they look identical from here. So we re-pull once the
// stream has been quiet this long, and rebuild the connection outright if it
// stays quiet — an SSE socket can stay open and simply stop delivering.
const STREAM_QUIET_MS = 12000;
const STREAM_REBUILD_MS = 45000;
const WATCHDOG_INTERVAL_MS = 5000;
// Hide the card on tap, but put it back if the POST never landed. Longer than
// the 60s client ask timeout so an in-flight answer is not flashed back.
const OPTIMISTIC_GATE_STALE_MS = 90000;
// Give the execution host a moment to finish closing the previous session
// before an explicitly queued follow-up opens the next one. Draining on the
// same render as session_end can race runner teardown and turn a valid queued
// message into an immediate "unexpected problem" pause.
// Native JSON poll cadence while the app is backgrounded.
// Nobody is looking, so spend the radio sparingly and catch up on return.
const BACKGROUND_POLL_MS = 30000;
const WEB_BACKGROUND_POLL_MS = 6000;

function appStateIsActive(state) {
  // RN Web reports 'active' | 'background' from document visibility; native
  // adds 'inactive' (app switcher) and 'unknown' during boot.
  return state !== 'background' && state !== 'inactive';
}

// High-frequency narration events are coalesced into one store write per
// window. Every SSE frame used to be its own Zustand set — under rapid tool
// churn (and on reconnect replay) that rebuilt the Work run projection and
// re-rendered the transcript list many times a second.
const EVENT_COALESCE_MS = 80;
// Events after which the server projection can hold more than the event
// log gives us (artifact Files, settled artifacts/error).
const DETAIL_REFRESH_EVENT_TYPES = new Set(['session_end', 'artifact_ready']);
const COALESCIBLE_EVENT_TYPES = new Set([
  'tool_execution_start',
  'tool_execution_end',
  'turn_start',
  'turn_end',
  'agent_start',
  'agent_end',
  'compaction_start',
  'compaction_end',
  'auto_retry_start',
  'auto_retry_end',
  'browser_screenshot',
]);

export function useCoworkerChat({ chat, conversationId }) {
  const router = useRouter();
  const [boundaryChecking, setBoundaryChecking] = useState(false);
  const boundarySendLock = useRef(false);
  const flaggedWork = !!(chat?.flags?.isCoworkerSession || chat?.coworkerSession);
  // Latch once Work so a store merge that drops the flag mid-resume cannot
  // flip the thread back to Chat and retrigger the handover fade in a loop.
  // Reset when this ChatView instance is reused for a different /c/:id.
  const workLatchRef = useRef({ latchedConversationId: conversationId, latchedWork: flaggedWork });
  workLatchRef.current = nextWorkLatch({
    conversationId,
    flaggedWork,
    latchedConversationId: workLatchRef.current.latchedConversationId,
    latchedWork: workLatchRef.current.latchedWork,
  });
  const isCoworkerChat = workLatchRef.current.latchedWork;
  // Select only the resolved id. Subscribing to the whole tasks map re-rendered
  // ChatView on every task:update (credits, tokens) and retriggered effects
  // until React hit Maximum update depth.
  const taskId = useCoworkerStore((s) => findTaskIdForConversation(conversationId, {
    chat,
    tasks: s.tasks,
    taskIds: s.taskIds,
  }));
  const isAuthenticated = !!useAuthStore((s) => s.token);

  const events = useCoworkerStore((s) => (taskId ? s.eventsByTask[taskId] : null) || NO_EVENTS);
  const transcriptEvents = useCoworkerStore((s) => (
    taskId ? s.transcriptEventsByTask[taskId] : null
  ) || NO_EVENTS);
  const liveDraft = useCoworkerStore((s) => (taskId ? s.liveDraftByTask[taskId] : null) || null);
  const pendingSends = useCoworkerStore((s) => (
    taskId ? s.pendingSendsByTask[taskId] : null
  ) || NO_ROWS);
  const retainedPlan = useCoworkerStore((s) => (
    taskId ? s.latestPlanByTask?.[taskId] : null
  ) || null);
  // The task record itself carries what the event stream cannot: the token
  // budget it is spending against and the reason a run stopped.
  const task = useCoworkerStore((s) => (taskId ? s.tasks[taskId] : null) || null);
  // Seed from what we already have so leaving and coming back does not
  // render a settled transcript for a run that is still going.
  const [taskStatus, setTaskStatus] = useState(() => seedWorkTaskStatus({
    task: taskId ? useCoworkerStore.getState().tasks[taskId] : null,
    events: taskId ? useCoworkerStore.getState().eventsByTask[taskId] : null,
    isInferencing: !!chat?.isInferencing,
  }));
  // Persisted "browser invoked" note from task detail — lets the open-
  // browser-view button appear right after a refresh (before the SSE
  // replay delivers the browser_* tool events).
  const [browserUsed, setBrowserUsed] = useState(false);
  const [llmStatus, setLlmStatus] = useState(null);
  const lastSeqRef = useRef(0);
  // When the run last told us anything, which is both what the watchdog acts
  // on and what the transcript shows when a run has gone quiet.
  const lastEventAtRef = useRef(Date.now());
  const lastStreamUiAtRef = useRef(0);
  const [lastEventAt, setLastEventAt] = useState(null);
  // Bumped to rebuild the live subscription from scratch.
  const [streamEpoch, setStreamEpoch] = useState(0);
  // Snapshot first so SSE replay cannot apply a stale session_end with
  // lastSeq still at 0 (follow-ups reconnect onto a log that already ended).
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const taskStatusRef = useRef(taskStatus);
  // Follow-up/resume has been requested; ignore stale "completed" snapshots
  // until the execution log shows a new running session.
  const expectLiveRef = useRef(false);
  const expectedAfterSeqRef = useRef(0);
  // This expectation is also UI state. A ref is enough to reject a stale
  // snapshot, but it cannot keep the Stop control / live status visible while
  // the host is accepting a follow-up. Without this, a submitted follow-up can
  // look completely idle until refresh even though it is already spending.
  const [expectLive, setExpectLive] = useState(false);
  const [optimisticLiveStartedAt, setOptimisticLiveStartedAt] = useState(null);
  // Ids the user already tapped. Hide those cards before /ask returns so a
  // slow execution host cannot leave Approve spinning on the spend gate.
  const [optimisticResolvedIds, setOptimisticResolvedIds] = useState([]);
  const optimisticResolvedAtRef = useRef({});
  const markExpectLive = useCallback((next) => {
    const value = !!next;
    if (value && !expectLiveRef.current) expectedAfterSeqRef.current = lastSeqRef.current;
    expectLiveRef.current = value;
    setExpectLive(value);
    setOptimisticLiveStartedAt((current) => (
      value ? (current || Date.now()) : null
    ));
  }, []);
  const detailFetchTimerRef = useRef(null);
  // One conversation GET at a time. Under an event burst the debounce timer
  // can fire while the previous GET is still in flight; instead of stacking
  // requests, remember that we owe one more and run it when this one lands.
  const detailFetchInFlightRef = useRef(false);
  const detailFetchDirtyRef = useRef(false);
  const scheduleDetailRefreshRef = useRef(null);
  // History open already fetched conversation detail. The first snapshot
  // must not immediately GET it again — that re-ran the full thread load
  // on every click into a long Work conversation.
  const skipNextDetailRefreshRef = useRef(false);
  // Startup can issue concurrent snapshot and fallback-poll requests. None
  // of those requests should notify for the history they are loading.
  const notificationsReadyRef = useRef(false);
  // Delayed post-settle refreshes: artifact Files materialize in the
  // background (R2 → CDN copy can take a while after the turn ends), and
  // the attach lands on a later backend sync — re-fetch the conversation a
  // few times so the inline previews swap in without a manual reload.
  const postSettleTimersRef = useRef([]);

  const fetchTask = useCoworkerStore((s) => s.fetchTask);
  const stopTask = useCoworkerStore((s) => s.stopTask);
  const sendMessage = useCoworkerStore((s) => s.sendMessage);
  const resumeTask = useCoworkerStore((s) => s.resumeTask);
  const ingestTaskEvents = useCoworkerStore((s) => s.ingestTaskEvents);
  const beginTaskFollow = useCoworkerStore((s) => s.beginTaskFollow);
  const endTaskFollow = useCoworkerStore((s) => s.endTaskFollow);

  const isActive = useMemo(
    () => expectLive || workIsActivelyRunning({
      taskStatus,
      task,
      events,
      isInferencing: !!chat?.isInferencing,
    }),
    [expectLive, taskStatus, task, events, chat?.isInferencing],
  );

  // Composer Stop is gated on chat.isInferencing, but the live trail and
  // "Send follow-up" placeholder follow isActive (event-log first). A stale
  // idle host snapshot used to clear isInferencing while the run was still
  // open, which hid the stop square and left only Dictate in the composer.
  useEffect(() => {
    if (!isCoworkerChat || !conversationId) return;
    if (!!chat?.isInferencing === isActive) return;
    useChatStore.getState().updateLocalChatDetails(conversationId, {
      isInferencing: isActive,
      isInferencingWaiting: false,
    });
  }, [isCoworkerChat, conversationId, isActive, chat?.isInferencing]);

  const ingestEventsNow = useCallback((incoming) => {
    if (!taskId || !Array.isArray(incoming) || incoming.length === 0) return;
    let advanced = false;
    for (const e of incoming) {
      if (Number(e?.seq) > lastSeqRef.current) {
        lastSeqRef.current = Number(e.seq);
        advanced = true;
      }
    }
    if (advanced) {
      const now = Date.now();
      lastEventAtRef.current = now;
      setLastEventAt(now);
    }
    ingestTaskEvents(taskId, incoming);
  }, [taskId, ingestTaskEvents]);

  // Coalescing buffer for high-frequency narration events. Draining before
  // any other ingest keeps the store's event array in arrival order — turn
  // building walks it sequentially.
  const pendingEventsRef = useRef([]);
  const pendingFlushTimerRef = useRef(null);
  const flushPendingEvents = useCallback(() => {
    if (pendingFlushTimerRef.current) {
      clearTimeout(pendingFlushTimerRef.current);
      pendingFlushTimerRef.current = null;
    }
    if (!pendingEventsRef.current.length) return;
    const batch = pendingEventsRef.current;
    pendingEventsRef.current = [];
    ingestEventsNow(batch);
  }, [ingestEventsNow]);

  const ingestEvents = useCallback((incoming) => {
    flushPendingEvents();
    ingestEventsNow(incoming);
  }, [flushPendingEvents, ingestEventsNow]);

  const enqueueEvent = useCallback((ev) => {
    if (COALESCIBLE_EVENT_TYPES.has(ev?.type)) {
      pendingEventsRef.current.push(ev);
      if (!pendingFlushTimerRef.current) {
        pendingFlushTimerRef.current = setTimeout(flushPendingEvents, EVENT_COALESCE_MS);
      }
      return;
    }
    // Lifecycle and interactive events land immediately (pending first, so
    // order holds) — handlers read the store synchronously right after.
    ingestEvents([ev]);
  }, [flushPendingEvents, ingestEvents]);

  // Debounced conversation-detail refetch: the backend lazily materializes
  // the event log into messages on this endpoint, so this pulls new bubbles
  // into the store without duplicating any projection logic client-side.
  const scheduleDetailRefresh = useCallback(() => {
    if (!conversationId) return;
    if (detailFetchTimerRef.current) clearTimeout(detailFetchTimerRef.current);
    detailFetchTimerRef.current = setTimeout(async () => {
      detailFetchTimerRef.current = null;
      if (detailFetchInFlightRef.current) {
        detailFetchDirtyRef.current = true;
        return;
      }
      detailFetchInFlightRef.current = true;
      try {
        const data = await chatsApi.getConversationById(conversationId);
        if (data) useChatStore.getState().addChats([data]);
      } catch { /* best-effort — next event or poll retries */ } finally {
        detailFetchInFlightRef.current = false;
        if (detailFetchDirtyRef.current) {
          detailFetchDirtyRef.current = false;
          scheduleDetailRefreshRef.current?.();
        }
      }
    }, 400);
  }, [conversationId]);
  scheduleDetailRefreshRef.current = scheduleDetailRefresh;

  const schedulePostSettleRefreshes = useCallback(() => {
    if (!conversationId && !taskId) return;
    for (const timer of postSettleTimersRef.current) clearTimeout(timer);
    postSettleTimersRef.current = [];
    // artifact_ready already triggers a detail refresh; one delayed pull
    // covers CDN/R2 copies that land a few seconds after session_end, and
    // the billed credit total that rolls up when the minted key is revoked.
    const timer = setTimeout(() => {
      scheduleDetailRefresh();
      if (taskId) void fetchTask(taskId);
    }, 5000);
    postSettleTimersRef.current.push(timer);
  }, [conversationId, taskId, fetchTask, scheduleDetailRefresh]);

  const applyStatus = useCallback((status) => {
    if (!status || status === taskStatusRef.current) return;
    taskStatusRef.current = status;
    setTaskStatus(status);
    if (conversationId) {
      const eventsNow = taskId
        ? (useCoworkerStore.getState().eventsByTask[taskId] || NO_EVENTS)
        : NO_EVENTS;
      const taskNow = taskId ? (useCoworkerStore.getState().tasks[taskId] || null) : null;
      // Same liveness as the live trail / steer placeholder — do not drop
      // isInferencing (composer Stop) just because a host snapshot is idle
      // while the event log still has an open session.
      const active = workIsActivelyRunning({
        taskStatus: status,
        task: taskNow,
        events: eventsNow,
        isInferencing: COWORKER_ACTIVE_STATUSES.includes(status),
      });
      useChatStore.getState().updateLocalChatDetails(conversationId, {
        isInferencing: active,
        isInferencingWaiting: false,
      });
    }
  }, [conversationId, taskId]);

  const pullSnapshot = useCallback(async ({ history = false } = {}) => {
    if (!taskId) return null;
    const startedAtSeq = lastSeqRef.current;
    const canNotify = notificationsReadyRef.current;
    const skipDetailRefresh = skipNextDetailRefreshRef.current;
    if (skipDetailRefresh) skipNextDetailRefreshRef.current = false;
    const data = await coworkerApi.fetchTaskEvents(taskId, history ? 0 : lastSeqRef.current).catch(() => null);
    if (!data) return null;
    notificationsReadyRef.current = true;
    const incoming = data.events || [];
    const mapped = data.status === 'completed' ? 'idle' : data.status;
    // SSE or another poll may have advanced the cursor while this request
    // was in flight. Only paint and notify for events still unseen now.
    const newEvents = incoming.filter(ev => Number(ev?.seq) > lastSeqRef.current);
    // Polling is the only transport when the native SSE stream is unavailable,
    // and it never carries live token frames — without this the whole reply
    // lands as one message_end block and shoves the transcript down the page.
    // Feed the assistant frames the stream would have delivered into the same
    // pacer so the official row still reveals progressively. The history load
    // replays settled replies that should appear whole, so skip it.
    if (!history) {
      for (const ev of newEvents) {
        if (ev?.type === 'message_end' && !hasOpenWorkAssistantDraft(taskId)) {
          // A poll can miss the matching message_start (it fell inside the
          // history window). Open a missing draft, but preserve any text
          // already revealed by SSE when its completion arrives by polling.
          applyWorkAssistantEvent(taskId, { type: 'message_start', message: ev.message });
        }
        applyWorkAssistantEvent(taskId, ev);
      }
    }
    if (canNotify) {
      soundForWorkEvents(newEvents);
      // We retain the watcher claim while hidden, so polling must also
      // provide its tab-title and desktop alerts. Ignore requests already
      // resolved within this batch, and let the event cursor dedupe replays.
      if (Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        const { loginRequest, confirmation, question } = workPendingInteractionFromEvents(newEvents);
        const taskNow = useCoworkerStore.getState().tasks[taskId];
        const chatNow = useChatStore.getState().chats.find(c => c.id === conversationId);
        const questionKind = question?.kind === 'credits' ? 'credits'
          : (['cost', 'memory', 'plan'].includes(question?.kind) ? 'approval' : 'question');
        const requests = [
          [loginRequest, 'login'],
          [confirmation, 'approval'],
          [question, questionKind],
        ];
        for (const [request, kind] of requests) {
          if (request) signalWorkTabAttention({
            kind,
            taskId,
            conversationId,
            threadTitle: taskNow?.title || taskNow?.name || chatNow?.title,
            playSound: false, // The event batch already owns the sound above.
          });
        }
      }
    }
    ingestEvents(incoming);
    if (Object.hasOwn(data, 'goal')) useCoworkerStore.getState().addTask({ id: taskId, goal: data.goal, goalEventSeq: data.goalEventSeq || 0 });
    // A resumed run can end before its first snapshot (for example during
    // a host restart). Seeing its new lifecycle boundary acknowledges the
    // request even when the host is already interrupted/completed again.
    if (expectLiveRef.current && (useCoworkerStore.getState().eventsByTask[taskId] || []).some(ev => (
      Number(ev.seq) > expectedAfterSeqRef.current
      && ['session_start', 'session_resume'].includes(ev.type)
    ))) markExpectLive(false);
    const lifecycle = workLifecycleStatusFromEvents(
      useCoworkerStore.getState().eventsByTask[taskId] || [],
    );
    // Host status wins over a stale session_start in the event log (the
    // runner dies on orchestrator restart with no session_end).
    const hostSettled = mapped && !COWORKER_ACTIVE_STATUSES.includes(mapped);
    const liveNow = hostSettled
      ? false
      : ((mapped && COWORKER_ACTIVE_STATUSES.includes(mapped)) || lifecycle === 'running');
    if (liveNow) {
      markExpectLive(false);
      applyStatus(mapped && COWORKER_ACTIVE_STATUSES.includes(mapped) ? mapped : 'running');
    } else if (!expectLiveRef.current) {
      if (mapped) applyStatus(mapped);
      else if (lifecycle) applyStatus(lifecycle);
    }
    const settled = !liveNow && !expectLiveRef.current
      && mapped && !COWORKER_ACTIVE_STATUSES.includes(mapped);
    // Rows come from the event log; the server only adds what events cannot
    // carry — materialized artifact Files and settled state.
    const advancedMaterialized = incoming.some((e) => (
      Number(e?.seq) > startedAtSeq && DETAIL_REFRESH_EVENT_TYPES.has(e.type)
    ));
    // A cold history open already has the conversation. Only refetch when
    // this pull actually advanced the projection, or a live turn is finishing
    // while we are still navigating in.
    if (advancedMaterialized && (!skipDetailRefresh || liveNow)) {
      scheduleDetailRefresh();
    }
    if (settled && incoming.some((e) => e.type === 'session_end') && startedAtSeq > 0) {
      schedulePostSettleRefreshes();
    }
    return data;
  }, [taskId, conversationId, applyStatus, ingestEvents, markExpectLive, scheduleDetailRefresh, schedulePostSettleRefreshes]);

  // Studio edits and exports send through the store, outside the composer.
  // The previous session_end otherwise keeps this hook idle forever, even
  // though the new turn is running and waiting on a question the user cannot see.
  const observedFollowUpRef = useRef({ taskId, version: task?.localFollowUpVersion || 0 });
  const localFollowUpVersion = task?.localFollowUpVersion || 0;
  useEffect(() => {
    const previous = observedFollowUpRef.current;
    observedFollowUpRef.current = { taskId, version: localFollowUpVersion };
    if (previous.taskId !== taskId || localFollowUpVersion <= previous.version) return;
    markExpectLive(true);
    applyStatus('running');
    setStreamEpoch((epoch) => epoch + 1);
    void pullSnapshot();
  }, [taskId, localFollowUpVersion, markExpectLive, applyStatus, pullSnapshot]);

  // Complete the transcript tail on open: every transcript event past the
  // server cursor, uncapped. The narration snapshot above is capped at 500
  // and a busy run emits more than that between syncs.
  const pullTranscriptTail = useCallback(async () => {
    if (!taskId) return;
    const chatNow = useChatStore.getState().chats.find((c) => c.id === conversationId);
    const since = workTranscriptSinceSeq(chatNow?.messages, chatNow?.workSync?.eventsSyncedSeq);
    const data = await coworkerApi.fetchTaskEvents(taskId, since, { kinds: 'transcript' }).catch(() => null);
    if (data?.events?.length) {
      useCoworkerStore.getState().ingestTranscriptEvents(taskId, data.events);
    }
  }, [taskId, conversationId]);

  const pullSnapshotRef = useRef(pullSnapshot);
  const pullTranscriptTailRef = useRef(pullTranscriptTail);
  const applyStatusRef = useRef(applyStatus);
  const flushPendingEventsRef = useRef(flushPendingEvents);
  useEffect(() => {
    pullSnapshotRef.current = pullSnapshot;
    pullTranscriptTailRef.current = pullTranscriptTail;
    applyStatusRef.current = applyStatus;
    flushPendingEventsRef.current = flushPendingEvents;
  }, [pullSnapshot, pullTranscriptTail, applyStatus, flushPendingEvents]);

  // Latest handlers for the live subscription. The stream effect reads
  // through this ref so a re-created callback can never tear down and
  // rebuild the SSE connection — only identity keys (task, liveness,
  // epoch) do that.
  const streamHandlersRef = useRef(null);
  streamHandlersRef.current = {
    enqueueEvent,
    scheduleDetailRefresh,
    schedulePostSettleRefreshes,
    applyStatus,
    fetchTask,
    pullSnapshot,
  };

  // A hidden web tab must release its long-lived API connection: browsers
  // share a small HTTP/1 connection pool across tabs, so several Work streams
  // can otherwise queue ordinary chat/detail requests behind them. Native
  // retains its stream and slower background polling. Hidden web tabs keep
  // polling for attention events instead of going silent until focus returns.
  const [appActive, setAppActive] = useState(() => appStateIsActive(AppState.currentState));
  const appActiveRef = useRef(appActive);
  useEffect(() => {
    const onChange = (next) => {
      const active = appStateIsActive(next);
      if (active === appActiveRef.current) return;
      appActiveRef.current = active;
      setAppActive(active);
    };
    const subscription = AppState.addEventListener('change', onChange);
    return () => {
      if (typeof subscription?.remove === 'function') subscription.remove();
      else AppState.removeEventListener?.('change', onChange);
    };
  }, []);
  const canFollowLive = Platform.OS !== 'web' || appActive;

  // Initial task fetch. Conversation detail is already on the route; only
  // re-pull it when the task is still live (a fast turn can finish while
  // we are navigating to /c/:id).
  useEffect(() => {
    if (!isCoworkerChat || !taskId || !isAuthenticated) {
      setHasSnapshot(false);
      return undefined;
    }
    let cancelled = false;
    lastSeqRef.current = 0;
    notificationsReadyRef.current = false;
    const stored = useCoworkerStore.getState().eventsByTask[taskId] || [];
    for (const ev of stored) {
      const seq = Number(ev?.seq) || 0;
      if (seq > lastSeqRef.current) lastSeqRef.current = seq;
    }
    // History already has the conversation. Do not hold first paint on the
    // event log — trails can attach after the list is visible.
    skipNextDetailRefreshRef.current = true;
    setHasSnapshot(true);
    // Re-seed on task change (the useState initializer only runs once).
    const seeded = seedWorkTaskStatus({
      task: useCoworkerStore.getState().tasks[taskId],
      events: useCoworkerStore.getState().eventsByTask[taskId],
      // Read live; do not list chat.isInferencing as a dep — Resume writes
      // that flag and would re-run this effect (reset lastSeq, refetch)
      // until React hits Maximum update depth.
      isInferencing: !!useChatStore.getState().chats.find((c) => c.id === conversationId)?.isInferencing,
    });
    if (seeded) applyStatusRef.current(seeded);
    // The sidebar/background watcher retains only status events. Its latest
    // seq is not proof that preceding tool activity has been loaded. Fetch
    // the bounded history window once on entry, then poll incrementally.
    void pullSnapshotRef.current({ history: true });
    void pullTranscriptTailRef.current();
    (async () => {
      const task = await fetchTask(taskId);
      if (cancelled) return;
      if (task) {
        const mapped = task.status === 'completed' ? 'idle' : task.status;
        const alreadyLive = COWORKER_ACTIVE_STATUSES.includes(taskStatusRef.current);
        if (!alreadyLive || COWORKER_ACTIVE_STATUSES.includes(mapped) || isWorkResumable(mapped)) {
          applyStatusRef.current(mapped);
        }
        if (task.browserUsed) setBrowserUsed(true);
        if (task.activityStatus) setLlmStatus(task.activityStatus);
        if (COWORKER_ACTIVE_STATUSES.includes(mapped)) {
          scheduleDetailRefresh();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isCoworkerChat, taskId, conversationId, isAuthenticated, fetchTask, scheduleDetailRefresh]);

  // Claim this task so CoworkerWatcher does not open a second EventSource
  // while the in-thread hook is the live subscriber.
  useEffect(() => {
    if (!isCoworkerChat || !taskId || !isActive) return undefined;
    beginTaskFollow(taskId);
    return () => endTaskFollow(taskId);
  }, [isCoworkerChat, taskId, isActive, beginTaskFollow, endTaskFollow]);

  // Live event stream: SSE where available, polling otherwise. Restarted
  // exactly when liveness flips (a resumed thread reconnects) or the epoch
  // is bumped — never because a handler closure was re-created.
  const activeKey = isActive ? 'active' : 'idle';
  useEffect(() => {
    if (!isCoworkerChat || !taskId || !isAuthenticated || !isActive || !hasSnapshot) return;
    let stopped = false;
    let pollTimer = null;
    let polling = false;
    const handlers = () => streamHandlersRef.current;
    const enqueueEvent = (ev) => handlers().enqueueEvent(ev);
    const scheduleDetailRefresh = () => handlers().scheduleDetailRefresh();
    const schedulePostSettleRefreshes = () => handlers().schedulePostSettleRefreshes();
    const applyStatus = (status) => handlers().applyStatus(status);
    const fetchTask = (id) => handlers().fetchTask(id);
    const pullSnapshot = () => handlers().pullSnapshot();

    const handleEvent = (ev) => {
      if (stopped || !ev) return;
      const isLiveDelta = ev.live === true || ev.type === 'message_update';
      const isNew = isNewWorkEvent(ev, lastSeqRef.current);
      // Historical SSE replay is a no-op for the transcript (seq already
      // ingested from the snapshot). Skip it before ingest / JSON slimming
      // work so a resume reconnect cannot stall the tab.
      if (!isLiveDelta && !isNew) return;
      if (isLiveDelta || (isNew && (ev.type === 'message_start' || ev.type === 'message_end'))) {
        applyWorkAssistantEvent(taskId, ev);
        const now = Date.now();
        lastEventAtRef.current = now;
        if (isLiveDelta) {
          if (now - lastStreamUiAtRef.current > 1000) {
            lastStreamUiAtRef.current = now;
            setLastEventAt(now);
          }
        } else {
          setLastEventAt(now);
        }
      }
      // Live deltas never enter the persisted event log — painting above is
      // all they do, so skip the store round-trip entirely.
      if (isLiveDelta && !isNew) return;
      enqueueEvent(ev);
      if (!isNew) return;
      soundForWorkEvents([ev]);
      if (ev.type === 'session_start' || ev.type === 'session_resume') {
        markExpectLive(false);
        applyStatus('running');
      }
      // user_message / message_end rows come straight from the event; only
      // settle and Files need the server projection.
      if (DETAIL_REFRESH_EVENT_TYPES.has(ev.type)) {
        scheduleDetailRefresh();
      }
      if (ev.type === 'session_end') {
        const lifecycle = workLifecycleStatusFromEvents(
          useCoworkerStore.getState().eventsByTask[taskId] || [],
        );
        if (lifecycle === 'running') return;
        applyStatus(ev.status === 'completed' ? 'idle' : ev.status);
        // Settled fields (artifacts, error) — one detail fetch for the task.
        void fetchTask(taskId).then((task) => {
          if (!task || stopped || expectLiveRef.current) return;
          applyStatus(task.status === 'completed' ? 'idle' : task.status);
        });
        // Files materialize in the background after settle — keep pulling
        // the conversation until the inline previews land.
        schedulePostSettleRefreshes();
      }
    };

    const startPolling = () => {
      if (stopped || polling) return;
      polling = true;
      const tick = async () => {
        if (stopped) return;
        const data = await pullSnapshot();
        if (stopped) return;
        const status = data?.status === 'completed' ? 'idle' : data?.status;
        // Only a snapshot that says the run has settled ends the loop. An
        // unexpected or missing status keeps polling: giving up here is how a
        // live run goes silent until the user reloads. Host status wins over
        // a stale session_start with no session_end.
        if (data && status && !COWORKER_ACTIVE_STATUSES.includes(status)) return;
        const delay = appActiveRef.current ? (data ? 1500 : 4000) : (Platform.OS === 'web' ? WEB_BACKGROUND_POLL_MS : BACKGROUND_POLL_MS);
        pollTimer = setTimeout(tick, delay);
      };
      tick();
    };

    const onEot = () => {
      scheduleDetailRefresh();
      void pullSnapshot().then((data) => {
        if (stopped || expectLiveRef.current) return;
        const mapped = data?.status === 'completed' ? 'idle' : data?.status;
        if (mapped && COWORKER_ACTIVE_STATUSES.includes(mapped)) {
          setStreamEpoch((epoch) => epoch + 1);
        }
      });
    };

    // Native has no EventSource. Poll so message_end / lastAssistantText
    // still paint, and also open a fetch SSE stream for live token frames.
    if (Platform.OS !== 'web' || !canFollowLive) startPolling();

    const closeStream = canFollowLive ? openWorkEventStream({
      getUrl: () => coworkerApi.getTaskEventsSseUrl(taskId, lastSeqRef.current),
      shouldSkipFrame: (data) => isStaleWorkSseFrame(data, lastSeqRef.current),
      onEvent: handleEvent,
      onEot,
      onFatal: startPolling,
    }) : null;

    return () => {
      stopped = true;
      closeStream?.();
      if (pollTimer) clearTimeout(pollTimer);
      clearWorkAssistantStream(taskId);
    };
    // `isActive` is intentionally encoded as `activeKey` so the subscription
    // restarts exactly on liveness flips rather than on every status change.
    // Handlers are read through streamHandlersRef, so they are not deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoworkerChat, taskId, conversationId, isAuthenticated, activeKey, streamEpoch, hasSnapshot, canFollowLive, markExpectLive]);

  // Reconcile on return, including a run completed while the tab was hidden.
  // The transcript pull is uncapped so a long absence cannot drop messages
  // beyond the narration snapshot. The web stream reopens above with its
  // retained cursor; native only rebuilds a stream that has gone quiet.
  const prevAppActiveRef = useRef(appActive);
  useEffect(() => {
    const wasActive = prevAppActiveRef.current;
    prevAppActiveRef.current = appActive;
    if (!appActive || wasActive) return;
    if (!isCoworkerChat || !taskId || !isAuthenticated) return;
    if (Platform.OS !== 'web' && !isActive) return;
    void pullSnapshotRef.current();
    if (Platform.OS === 'web') {
      void pullTranscriptTailRef.current();
    } else if (Date.now() - lastEventAtRef.current >= STREAM_QUIET_MS) {
      lastEventAtRef.current = Date.now();
      setStreamEpoch((epoch) => epoch + 1);
    }
  }, [appActive, isCoworkerChat, taskId, isAuthenticated, isActive]);

  // Watchdog. Messages only reach the transcript when an event tells us to
  // refetch the conversation, so a stream that stops delivering leaves the
  // thread frozen on its last known status while the run carries on — the
  // agent looks stuck when it is working fine, and a reload "fixes" it. This
  // keeps pulling on its own, and rebuilds a stream that stays quiet. It
  // sleeps while the app is backgrounded; the foreground effect above
  // catches up instead.
  useEffect(() => {
    if (!isCoworkerChat || !taskId || !isAuthenticated || !isActive || !appActive) return undefined;
    lastEventAtRef.current = Date.now();
    const timer = setInterval(() => {
      const quietFor = Date.now() - lastEventAtRef.current;
      if (quietFor < STREAM_QUIET_MS) return;
      // pullSnapshot already refreshes conversation detail when the snapshot
      // actually advanced or settled — do not GET the full thread on every tick.
      void pullSnapshot();
      if (quietFor >= STREAM_REBUILD_MS) {
        lastEventAtRef.current = Date.now();
        setStreamEpoch((epoch) => epoch + 1);
      }
    }, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isCoworkerChat, taskId, isAuthenticated, isActive, appActive, pullSnapshot]);

  // Browser coworker state derived from the event stream: the last login
  // request still awaiting a hand-back, and any confirmation awaiting the
  // user's approve/deny.
  const dismissPendingGate = useCallback((id) => {
    const key = String(id || '').trim();
    if (!key) return;
    optimisticResolvedAtRef.current[key] = Date.now();
    setOptimisticResolvedIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const restorePendingGate = useCallback((id) => {
    const key = String(id || '').trim();
    if (!key) return;
    delete optimisticResolvedAtRef.current[key];
    setOptimisticResolvedIds((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : prev));
  }, []);

  useEffect(() => {
    optimisticResolvedAtRef.current = {};
    setOptimisticResolvedIds([]);
  }, [taskId]);

  useEffect(() => {
    if (!optimisticResolvedIds.length) return;
    const fromEvents = workPendingInteractionFromEvents(events);
    const fromTranscript = transcriptEvents.length
      ? workPendingInteractionFromEvents(transcriptEvents)
      : fromEvents;
    const live = new Set([
      fromEvents.confirmation?.id,
      fromTranscript.question?.id || fromEvents.question?.id,
    ].filter(Boolean));
    setOptimisticResolvedIds((prev) => {
      const next = prev.filter((id) => live.has(id));
      if (next.length !== prev.length) {
        prev.forEach((id) => {
          if (!next.includes(id)) delete optimisticResolvedAtRef.current[id];
        });
      }
      return next.length === prev.length ? prev : next;
    });
  }, [events, transcriptEvents, optimisticResolvedIds]);

  useEffect(() => {
    if (!optimisticResolvedIds.length) return undefined;
    const timer = setInterval(() => {
      const staleBefore = Date.now() - OPTIMISTIC_GATE_STALE_MS;
      setOptimisticResolvedIds((prev) => {
        const next = prev.filter((id) => (optimisticResolvedAtRef.current[id] || 0) > staleBefore);
        if (next.length === prev.length) return prev;
        prev.forEach((id) => {
          if (!next.includes(id)) delete optimisticResolvedAtRef.current[id];
        });
        return next;
      });
    }, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [optimisticResolvedIds]);

  const browserState = useMemo(() => {
    // The activity log (events) carries browser_login_requested and
    // browser_confirmation_requested, but is sliced to MAX_EVENTS_PER_TASK.
    // Questions live on the uncapped transcript log so a long run never
    // drops a pending ask and leaves "Waiting for your answer" with no card.
    const fromEvents = workPendingInteractionFromEvents(events, { resolvedIds: optimisticResolvedIds });
    if (!transcriptEvents.length) return fromEvents;
    const fromTranscript = workPendingInteractionFromEvents(transcriptEvents, {
      resolvedIds: optimisticResolvedIds,
    });
    return {
      loginRequest: fromEvents.loginRequest,
      confirmation: fromEvents.confirmation,
      question: fromTranscript.question !== undefined ? fromTranscript.question : fromEvents.question,
    };
  }, [transcriptEvents, events, optimisticResolvedIds]);

  // The update_plan checklist the organizer published, latest revision.
  const plan = useMemo(() => workPlanFromEvents(events, retainedPlan), [events, retainedPlan]);

  // Drives the inline "Open cloud browser" card: once the run has touched the
  // browser, watching it is useful even when no login was requested.
  const hasBrowserActivity = useMemo(
    () => browserUsed || events.some((e) => String(e?.toolName || '').startsWith('browser_')),
    [browserUsed, events],
  );
  const [browserClock, setBrowserClock] = useState(0);
  const liveBrowser = useMemo(() => workBrowserActivity(events, {
    active: isActive,
    pendingBrowser: hasPendingBrowserInteraction(browserState),
  }), [events, isActive, browserState.loginRequest, browserState.confirmation, browserClock]);
  useEffect(() => {
    if (!liveBrowser.expiresAt) return undefined;
    const timer = setTimeout(() => setBrowserClock(value => value + 1), Math.max(1, liveBrowser.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [liveBrowser.expiresAt, taskId]);

  const activity = useMemo(
    () => workActivityMetaFromEvents(events, {
      llmStatus: llmStatus || chat?.pendingInferenceStatus,
      taskStatus,
      browserState,
    }),
    [events, llmStatus, taskStatus, browserState, chat?.pendingInferenceStatus],
  );
  const activityStatus = activity?.text || null;
  const activityIcon = activity?.icon || null;
  const activityStartedAt = activity?.startedAt || null;

  const queuedDirectionCount = useMemo(
    () => workQueuedDirectionCount(events),
    [events],
  );

  const followUpQueue = useCoworkerStore((s) => (
    taskId ? s.followUpQueueByTask[taskId] : null
  ) || NO_FOLLOWUPS);

  const enqueueFollowUp = useCallback((message, fileIds, options) => {
    const text = String(message || '').trim();
    if (!taskId || !text) return false;
    const queued = useCoworkerStore.getState().enqueueFollowUp(taskId, {
      text,
      fileIds,
    }, options);
    if (!queued) return false;
    if (conversationId) useChatStore.getState().clearFollowUpSuggestions(conversationId);
    return true;
  }, [taskId, conversationId]);

  const updateFollowUp = useCallback((id, patch) => {
    if (!taskId || !id) return;
    useCoworkerStore.getState().updateFollowUp(taskId, id, patch);
  }, [taskId]);

  const removeFollowUp = useCallback((id) => {
    if (!taskId || !id) return;
    useCoworkerStore.getState().removeFollowUp(taskId, id);
  }, [taskId]);

  const reorderFollowUp = useCallback((fromIndex, toIndex) => {
    if (!taskId) return;
    useCoworkerStore.getState().reorderFollowUp(taskId, fromIndex, toIndex);
  }, [taskId]);

  const clearFollowUps = useCallback(() => {
    if (!taskId) return;
    useCoworkerStore.getState().clearFollowUps(taskId);
  }, [taskId]);

  // Composer send: steer a live run, or start a follow-up turn on an idle
  // thread. The bubble shows immediately as a pending send and is replaced
  // by the `user_message` row once the host logs it (matched on text, only
  // against events newer than the send). fileIds: composer attachments —
  // validated server-side and injected into the agent prompt as
  // fetch_url-able CDN URLs. clientId lets a queued follow-up keep its row
  // key when it is sent.
  const handoffReview = useCoworkerStore(s => s.handoffReviewByTask?.[taskId] || null);
  const setHandoffReview = useCallback(review => useCoworkerStore.getState().setHandoffReview(taskId, review), [taskId]);
  useEffect(() => {
    if (!isCoworkerChat || !taskId) return;
    let active = true;
    coworkerApi.fetchTaskHandoffs(taskId).then(result => {
      const pending = result.handoffs?.find(row => ['pending', 'launching', 'failed'].includes(row.state)
        && row.decision?.action !== 'continue');
      if (active && pending) setHandoffReview(pending);
    }).catch(() => {});
    return () => { active = false; };
  }, [taskId, isCoworkerChat, setHandoffReview]);

  const send = useCallback(async (message, fileIds = [], options = {}) => {
    const { clientId, requestId: requestedId, ...routingOptions } = options;
    const text = String(message || '').trim();
    if (!text || !taskId) return false;
    if (text.toLowerCase() === '/undo' && fileIds.length === 0) {
      await openWorkUndo({ taskId, platform: Platform.OS, getState: useCoworkerStore.getState,
        modals: useModalStore.getState().actions,
        onError: message => useToastStore.getState().actions.addToast(message, 'error'),
        onRestored: async () => {
          if (conversationId) {
            const chat = await chatsApi.getConversationById(conversationId).catch(() => null);
            if (chat) useChatStore.getState().addChats([chat]);
          }
        },
      });
      return true;
    }
    if (boundarySendLock.current) return false;
    if (handoffReview && !routingOptions.routingChoice) throw new Error('Finish or cancel the new-task card before sending another message.');
    if (isWorkScriptApprovalQuestion(browserState?.question) && isWorkBlindContinue(text)) {
      throw new Error('Approve or Revise the clip prompts on the card — a continue would skip that review.');
    }
    const files = filesFromIds(fileIds);
    const wasActive = COWORKER_ACTIVE_STATUSES.includes(taskStatus);
    const requestId = workBoundaryRequestId(taskId, text, fileIds, requestedId || clientId);
    const optimisticId = clientId || requestId;
    boundarySendLock.current = true;
    setBoundaryChecking(true);
    useCoworkerStore.getState().addPendingSend(taskId, {
      id: optimisticId, text, files, createdAt: Date.now(), afterSeq: lastSeqRef.current,
    });
    if (conversationId) useChatStore.getState().clearFollowUpSuggestions(conversationId);
    try {
      // Backend owns placement before either a live steer or an idle resume.
      const out = await sendMessage(taskId, text, fileIds, { requestId, ...routingOptions });
      if (out.goalControl || out.stopControl) {
        useCoworkerStore.getState().removePendingSend(taskId, optimisticId);
        finishWorkBoundaryRequest(requestId);
        markExpectLive(false);
        applyStatus(out.status);
        void pullSnapshot();
        return true;
      }
      if (out.routing) {
        useCoworkerStore.getState().removePendingSend(taskId, optimisticId);
        markExpectLive(false);
        const destination = boundaryDestination(out);
        if (destination) {
          setHandoffReview(null);
          finishWorkBoundaryRequest(requestId);
          const collectionId = destination.collectionId
            || useChatStore.getState().chats.find(c => c.id === conversationId)?.inCollectionId;
          useCoworkerStore.getState().registerHandoffDestination(
            collectionId ? { ...destination, collectionId } : destination,
          );
          scheduleDetailRefresh();
          navigateToCreatedWork(router, destination);
        } else {
          setHandoffReview(out.routing);
        }
        return true;
      }
      setHandoffReview(null);
      finishWorkBoundaryRequest(requestId);
      applyStatus(out.status || 'running');
      if (!wasActive && (!out.duplicate || COWORKER_ACTIVE_STATUSES.includes(out.status))) {
        markExpectLive(true);
        setStreamEpoch(epoch => epoch + 1);
      }
      void pullSnapshot();
      return true;
    } catch (err) {
      useCoworkerStore.getState().removePendingSend(taskId, optimisticId);
      markExpectLive(false);
      throw err;
    } finally {
      boundarySendLock.current = false;
      setBoundaryChecking(false);
    }
  }, [taskId, taskStatus, conversationId, sendMessage, applyStatus, markExpectLive, pullSnapshot, browserState?.question, handoffReview, setHandoffReview, router, scheduleDetailRefresh]);

  const controlGoal = useCallback(async (action, goal) => {
    const current = useCoworkerStore.getState().tasks[taskId];
    const out = await coworkerApi.controlGoal(taskId, action, goal, current?.activeTurnId);
    useCoworkerStore.getState().addTask({ id: taskId, goal: out.goal, goalEventSeq: out.goalEventSeq || 0, status: out.status });
    markExpectLive(false);
    applyStatus(out.status);
    void pullSnapshot();
  }, [taskId, applyStatus, markExpectLive, pullSnapshot]);

  const resolveHandoff = useCallback(async (choice, details = {}) => {
    if (!handoffReview) return;
    return send(handoffReview.message, handoffReview.fileIds || [], {
      requestId: handoffReview.requestId, routingChoice: choice, ...details,
    });
  }, [handoffReview, send]);
  const cancelHandoff = useCallback(async () => {
    if (!handoffReview) return;
    await coworkerApi.cancelTaskHandoff(taskId, handoffReview.requestId);
    finishWorkBoundaryRequest(handoffReview.requestId);
    setHandoffReview(null);
  }, [taskId, handoffReview, setHandoffReview]);
  const prepareHandoff = useCallback(async (message, fileIds = [], sourceMessageId) => {
    const requestId = workBoundaryRequestId(taskId, message, fileIds);
    const out = await coworkerApi.prepareTaskBoundary(taskId, { message, fileIds, requestId, manual: true, sourceMessageId });
    setHandoffReview(out.routing);
  }, [taskId, setHandoffReview]);

  const sendFollowUpNow = useCallback(async (item) => {
    const text = String(item?.text || '').trim();
    const fileIds = Array.isArray(item?.fileIds) ? item.fileIds : [];
    if (!text && !fileIds.length) return false;
    if (item?.id && taskId) {
      useCoworkerStore.getState().removeFollowUp(taskId, item.id);
    }
    try {
      return await send(text, fileIds, item?.id ? { clientId: workFollowUpMessageId(item.id) } : {});
    } catch (err) {
      if (taskId) {
        useCoworkerStore.getState().enqueueFollowUp(taskId, {
          ...(item?.id ? { id: item.id } : {}),
          text,
          fileIds,
          createdAt: item?.createdAt,
        }, { front: true });
      }
      throw err;
    }
  }, [send, taskId]);

  const resume = useCallback(async () => {
    if (!taskId) return;
    // Ignore a stale idle snapshot while the host is coming back up.
    // Conversation detail is pulled when session_resume / user_message land
    // — a full-thread GET here is what froze long Work chats on Resume.
    markExpectLive(true);
    try {
      const goal = useCoworkerStore.getState().tasks[taskId]?.goal;
      if (goal && ['paused', 'blocked'].includes(goal.status)) {
        await resumeTask(taskId, '/goal resume', [], { goalId: goal.id, goalRevision: goal.revision });
      } else await resumeTask(taskId);
      applyStatus('running');
      setStreamEpoch((epoch) => epoch + 1);
      void pullSnapshot();
    } catch (err) {
      markExpectLive(false);
      throw err;
    }
  }, [taskId, resumeTask, applyStatus, markExpectLive, pullSnapshot]);

  const stop = useCallback(async () => {
    if (!taskId) return;
    await stopTask(taskId);
    markExpectLive(false);
    applyStatus('stopped');
    scheduleDetailRefresh();
  }, [taskId, stopTask, applyStatus, markExpectLive, scheduleDetailRefresh]);

  useEffect(() => () => {
    if (detailFetchTimerRef.current) clearTimeout(detailFetchTimerRef.current);
    // The event store outlives this hook (background watcher, re-entry into
    // the thread) — push any buffered narration in rather than dropping it.
    flushPendingEventsRef.current?.();
    for (const timer of postSettleTimersRef.current) clearTimeout(timer);
    postSettleTimersRef.current = [];
  }, []);

  // The transcript. One pure projection over the server rows and the live
  // sources; row objects keep identity between projections when unchanged
  // so the list never re-keys or re-renders rows that did not move.
  const transcriptMemoRef = useRef(null);
  if (!transcriptMemoRef.current) transcriptMemoRef.current = createWorkTranscriptMemo();
  const serverMessages = chat?.messages;
  const syncedSeq = chat?.workSync?.eventsSyncedSeq;
  const archivedMessageCount = chat?.plusMessageLock ? undefined : chat?.contextWindow?.archivedMessageCount;
  const taskPrompt = task?.prompt;
  const adoptedUserTurn = task?.adoptedUserTurn === true;
  useEffect(() => {
    if (!isCoworkerChat || !taskId || !pendingSends.length) return;
    // A detail fetch can beat/miss the live acknowledgement. Permanently
    // remove its placeholder before a later sync archives the matching row.
    useCoworkerStore.getState().settlePendingSends(taskId, transcriptEvents, serverMessages);
  }, [isCoworkerChat, taskId, pendingSends, transcriptEvents, serverMessages]);
  const transcript = useMemo(() => {
    if (!isCoworkerChat || !taskId) return null;
    return transcriptMemoRef.current(projectWorkTranscript({
      taskId,
      serverMessages: serverMessages || NO_ROWS,
      syncedSeq,
      archivedMessageCount,
      transcriptEvents,
      liveDraft,
      pendingSends,
      taskPrompt,
      adoptedUserTurn,
      settled: !isActive && !!taskStatus,
    }));
  }, [isCoworkerChat, taskId, serverMessages, syncedSeq, archivedMessageCount, transcriptEvents, liveDraft, pendingSends, taskPrompt, adoptedUserTurn, isActive, taskStatus]);

  return {
    isCoworkerChat,
    taskId,
    task,
    transcript,
    taskStatus,
    hasSnapshot,
    isActive,
    activityStatus,
    activityIcon,
    activityStartedAt,
    queuedDirectionCount,
    followUpQueue,
    optimisticLiveStartedAt,
    lastEventAt,
    events,
    browserUsed,
    browserState,
    dismissPendingGate,
    restorePendingGate,
    plan,
    hasBrowserActivity,
    isBrowserActive: liveBrowser.live,
    send,
    controlGoal,
    handoffReview,
    boundaryChecking,
    resolveHandoff,
    cancelHandoff,
    prepareHandoff,
    sendFollowUpNow,
    enqueueFollowUp,
    updateFollowUp,
    removeFollowUp,
    reorderFollowUp,
    clearFollowUps,
    stop,
    resume,
  };
}
